/**
 * Find & replace — pure, deterministic text replacement.
 * Safe to render server-side.
 */

export interface ReplaceOptions {
  caseInsensitive?: boolean;
  /** Treat `find` as a regular expression. */
  useRegex?: boolean;
  /** Match whole words only (literal mode). */
  wholeWord?: boolean;
}

export type ReplaceResult =
  | { ok: true; text: string; count: number }
  | { ok: false; error: string };

const SPECIAL = /[.*+?^${}()|[\]\\]/g;

function escapeRegex(s: string): string {
  return s.replace(SPECIAL, "\\$&");
}

export function findReplace(
  text: string,
  find: string,
  replace: string,
  opts: ReplaceOptions = {},
): ReplaceResult {
  if (find === "") return { ok: true, text, count: 0 };

  const flags = `g${opts.caseInsensitive ? "i" : ""}`;
  let re: RegExp;
  try {
    if (opts.useRegex) {
      re = new RegExp(find, flags);
    } else {
      let pattern = escapeRegex(find);
      if (opts.wholeWord) pattern = `\\b${pattern}\\b`;
      re = new RegExp(pattern, flags);
    }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : "Invalid pattern",
    };
  }

  const matches = text.match(re);
  const count = matches ? matches.length : 0;
  // In literal mode, escape `$` so replacements like "$&" stay literal;
  // in regex mode, `$1`/`$&` backreferences are honored.
  const repl = opts.useRegex ? replace : replace.replace(/\$/g, "$$$$");
  return { ok: true, text: text.replace(re, repl), count };
}
